import { DEFAULT_CORPORATE_DESIGN, ICorporateDesign } from '@web/dtos';
import { CMS_THEMES } from './cms-theme';
import { ThemeContext, ThemeMode, ThemeTokens } from './theme.types';

/** A draft may omit values that have not been loaded or edited yet. */
export type ThemeDesign = Partial<Omit<ICorporateDesign, 'colorAdmixtures'>> & {
  colorAdmixtures?: Partial<ICorporateDesign['colorAdmixtures']>;
};

export function resolveTheme(
  context: ThemeContext,
  mode: ThemeMode,
  design?: ThemeDesign | null
): ThemeTokens {
  if (context === 'cms') {
    return { ...CMS_THEMES[mode] };
  }

  const defaults = DEFAULT_CORPORATE_DESIGN;
  // Undefined draft fields should not erase defaults; zero rounding is valid.
  const value = <K extends keyof ICorporateDesign>(
    key: K
  ): ICorporateDesign[K] =>
    (design?.[key] ?? defaults[key]) as ICorporateDesign[K];
  const admixture = (key: keyof ICorporateDesign['colorAdmixtures']): string =>
    design?.colorAdmixtures?.[key] ?? defaults.colorAdmixtures[key];
  const dark = mode === 'dark';
  const primary = value(dark ? 'primaryDarkColor' : 'primaryColor');
  const secondary = value(dark ? 'secondaryDarkColor' : 'secondaryColor');
  const text = admixture(dark ? 'dark_text_high' : 'standard_text_high');
  const link = value(dark ? 'linkIconDark' : 'linkIcon');
  const radius = `${Math.max(0, value('buttonBorderRounding'))}px`;
  const shade = (color: string, percentage: number): string =>
    `color-mix(in srgb, ${color} ${percentage}%, black)`;

  return {
    background: admixture(dark ? 'dark_background' : 'standard_background'),
    surface: admixture(dark ? 'dark_surface_01' : 'standard_background'),
    'surface-raised': admixture(
      dark ? 'dark_surface_04' : 'standard_background'
    ),
    'text-primary': text,
    'text-secondary': admixture(dark ? 'dark_text' : 'standard_text'),
    'text-disabled': admixture(
      dark ? 'dark_text_disabled' : 'standard_text_disabled'
    ),
    border: admixture(dark ? 'dark_surface_end' : 'standard_text_outlined'),
    divider: admixture(dark ? 'dark_surface_02' : 'standard_text_divider'),
    hover: dark
      ? admixture('dark_surface_08')
      : `color-mix(in srgb, ${primary} 12%, ${admixture('standard_background')})`,
    selected: `color-mix(in srgb, ${primary} 20%, ${admixture(dark ? 'dark_surface_01' : 'standard_background')})`,
    link,
    'focus-ring': value(
      dark ? 'primaryDarkAlternativeColor' : 'primaryAlternativeColor'
    ),
    'control-background': admixture(
      dark ? 'dark_surface_01' : 'standard_background'
    ),
    'control-text': admixture(dark ? 'dark_text' : 'standard_text'),
    // The current inputs retain their standard border until focused.
    'control-border': 'var(--aw-cms-color-input-ring)',
    'control-placeholder': admixture(dark ? 'dark_text' : 'standard_text'),
    // Match aw-button's shared disabled styling in both shop modes.
    'disabled-background': 'var(--aw-cms-color-background-disabled)',
    'disabled-border': 'var(--aw-cms-color-background-disabled)',
    'disabled-text': 'var(--aw-cms-color-standard-text-disabled)',
    'action-background': primary,
    'action-text': value(dark ? 'primaryDarkFontColor' : 'primaryFontColor'),
    'action-border': value(
      dark ? 'primaryDarkBorderColor' : 'primaryBorderColor'
    ),
    'action-outline': value(
      dark ? 'primaryDarkAlternativeColor' : 'primaryAlternativeColor'
    ),
    'action-hover-background': shade(primary, 88),
    'action-active-background': shade(primary, 76),
    'secondary-background': secondary,
    'secondary-text': value(
      dark ? 'secondaryDarkFontColor' : 'secondaryFontColor'
    ),
    'secondary-border': value(
      dark ? 'secondaryDarkBorderColor' : 'secondaryBorderColor'
    ),
    'secondary-outline': value(
      dark ? 'secondaryDarkAlternativeColor' : 'secondaryAlternativeColor'
    ),
    'secondary-hover-background': shade(secondary, 88),
    // Buttons and form errors currently share this fixed red in both modes.
    error: '#CE4545',
    'error-background': `color-mix(in srgb, #CE4545 12%, ${admixture(dark ? 'dark_surface_01' : 'standard_background')})`,
    'on-error': '#FFFFFF',
    'error-hover': shade('#CE4545', 88),
    success: admixture('standard_success'),
    'control-radius': radius,
    'button-radius': radius,
  };
}
